import React, { useState, useEffect } from 'react';
import * as Icons from 'lucide-react';
import { useTranslation } from '../../hooks/useTranslation';
import { useAuth } from '../../contexts/AuthContext';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { toast } from 'react-hot-toast';
import { nanoid } from 'nanoid';

export default function ReferralLinkGenerator() {
  const { t } = useTranslation();
  const { currentUser } = useAuth();
  const [copied, setCopied] = useState(false);
  const [referralCode, setReferralCode] = useState('');

  useEffect(() => {
    async function fetchReferralCode() {
      if (!currentUser) return;
      
      try {
        const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
        if (userDoc.exists()) {
          const userData = userDoc.data();
          if (!userData.username) {
            const username = `user${currentUser.uid.slice(0, 6)}`;
            await updateDoc(doc(db, 'users', currentUser.uid), {
              username: username,
              updatedAt: new Date()
            });
            setReferralCode(username);
          } else {
            setReferralCode(userData.username);
          }
        }
      } catch (error) {
        console.error('Error fetching username:', error);
        toast.error(t('referral.usernameError'));
      }
    }
    
    fetchReferralCode();
  }, [currentUser, t]);

  const referralLink = `${window.location.origin}/register/${referralCode}`;

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(referralLink);
      setCopied(true);
      toast.success(t('common.copied'));
      
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error(t('referral.copyError'));
    }
  };

  const shareViaEmail = () => {
    const subject = encodeURIComponent(t('referral.emailSubject'));
    const body = encodeURIComponent(
      t('referral.emailBody', { link: referralLink })
    );
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };

  const shareViaWhatsApp = () => {
    const text = encodeURIComponent(
      t('referral.whatsappMessage', { link: referralLink })
    );
    window.open(`https://wa.me/?text=${text}`, '_blank');
  };

  return (
    <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-medium text-gray-900">
          {t('referral.title')}
        </h2>
        <div className="p-2 bg-blue-50 rounded-lg">
          <Icons.Users className="h-5 w-5 text-blue-500" />
        </div>
      </div>

      <div className="space-y-4">
        <div className="relative">
          <input
            type="text"
            readOnly
            value={referralLink}
            className="w-full pr-20 py-2 px-3 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-600"
          />
          <button
            onClick={copyToClipboard}
            className="absolute right-1 top-1 px-3 py-1 bg-white border border-gray-200 rounded-md text-sm font-medium text-gray-600 hover:bg-gray-50"
          >
            {copied ? (
              <Icons.Check className="h-4 w-4 text-green-500" />
            ) : (
              <Icons.Copy className="h-4 w-4" />
            )}
          </button>
        </div>

        <div className="flex gap-2">
          <button
            onClick={shareViaEmail}
            className="flex-1 flex items-center justify-center gap-2 py-2 px-4 bg-gray-50 hover:bg-gray-100 text-gray-700 rounded-lg text-sm font-medium"
          >
            <Icons.Mail className="h-4 w-4" />
            {t('referral.shareEmail')}
          </button>
          <button
            onClick={shareViaWhatsApp}
            className="flex-1 flex items-center justify-center gap-2 py-2 px-4 bg-green-50 hover:bg-green-100 text-green-700 rounded-lg text-sm font-medium"
          >
            <Icons.MessageCircle className="h-4 w-4" />
            WhatsApp
          </button>
        </div>

        <div className="text-sm text-gray-500">
          <p>{t('referral.description')}</p>
        </div>
      </div>
    </div>
  );
} 